/**
 * Rate card — the fixed price list for placements (the starting prices a
 * partnership page's Placements tab copies), and the yearly moments calendar (how many slots of each placement
 * we sell per moment). Everything is editable in place; prices are
 * transparent by design (no bidding). The per-brand deal sheet reads
 * from these tables.
 */
import { useEffect, useMemo, useState } from 'react';
import { Button, IconButton } from '../components/ui/Button';
import { Card, CardHead } from '../components/ui/Card';
import { Checkbox } from '../components/ui/Checkbox';
import { CommitInput } from '../components/ui/CommitInput';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { Field } from '../components/ui/Field';
import { Icon } from '../components/ui/Icon';
import { Modal } from '../components/ui/Modal';
import { useToast } from '../components/ui/Toast';
import {
  KIND_LABEL,
  Moment,
  MomentKind,
  PlacementRate,
  fmtEur,
  useMomentMutations,
  useMoments,
  useRateMutations,
  useRates,
} from '../lib/api/rateCard';
import { ASSET_CATEGORIES, ASSET_TYPES_BY_CATEGORY, AssetCategory } from '../lib/types';
import { CATEGORY_COLOR } from '../lib/categoryColors';
import MapEditor from '../components/placements/MapEditor';
import { HowItWorksLink } from '../components/ui/HowItWorksLink';
import MockupGallery from '../components/placements/MockupGallery';
import { STAT_LABEL, statLabelsFor, toCount } from '../lib/stats';
import { useMapPages } from '../lib/api/placementMap';

type Tab = 'rates' | 'moments' | 'map';
const TAB_KEY = 'mosy-rate-card-tab';
const KINDS = Object.keys(KIND_LABEL) as MomentKind[];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const STYLES = `
.rate-card .pp-intro { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; margin: 0 0 18px; }
.rate-card .pp-intro p { flex: 1 1 420px; margin: 0; font-size: 13px; color: var(--ink-3); line-height: 1.55; }
.rate-card .pp-intro .count { font-size: 12px; font-weight: 600; padding: 3px 10px; border-radius: 999px; background: #e6f4ea; color: #1e7a3c; }
.rate-card .pp-cat { --sec: var(--ink-4); margin-bottom: 18px; }
.rate-card .pp-cat .section-head { margin-bottom: 8px; }
.rate-card .pp-cat-title { display: flex; align-items: center; gap: 8px; color: var(--ink-3); }
.rate-card .pp-cat-title .dot { width: 6px; height: 6px; border-radius: 50%; background: var(--sec); }
.rate-card .pp-cat-card { border-left: 3px solid var(--sec); }
.rate-card .pp-row { display: grid; grid-template-columns: minmax(220px, 1.5fr) minmax(200px, 1.2fr) 150px 70px 36px; gap: 10px; align-items: center; padding: 8px 16px; border-bottom: 1px solid var(--border-soft); }
.rate-card .pp-row:last-child { border-bottom: 0; }
.rate-card .pp-row.off > *:not(:nth-child(4)) { opacity: 0.5; }
.rate-card .pp-head { font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em; color: var(--ink-4); font-weight: 600; padding-top: 10px; padding-bottom: 8px; border-bottom: 1px solid var(--border); }
.rate-card .pp-row .num { text-align: right; }
.rate-card .perday { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin: 2px 0 2px 6px; font-size: 11.5px; color: var(--ink-4); }
.rate-card .perday label { display: inline-flex; align-items: center; gap: 4px; }
.rate-card .perday .cell-input { width: 74px; padding: 2px 6px; font-size: 12px; text-align: right; }
.rate-card .maxpb { display: flex; justify-content: flex-end; align-items: center; gap: 4px; margin-top: 4px; font-size: 11px; color: var(--ink-4); }
.rate-card .maxpb .cell-input { width: 38px; text-align: center; padding: 2px 4px; font-size: 12px; }
.rate-card .cell-input.name { width: 100%; text-align: left; font-weight: 600; color: var(--ink); font-size: 13.5px; }
.rate-card .cell-input.desc { width: 100%; text-align: left; font-size: 12px; color: var(--ink-4); margin-top: 1px; }
.rate-card .cell-input.req { width: 100%; text-align: left; font-size: 12.5px; }
.rate-card .eur { position: relative; display: inline-block; }
.rate-card .eur .cur { position: absolute; left: 9px; top: 50%; transform: translateY(-50%); font-size: 12.5px; color: var(--ink-4); pointer-events: none; }
.rate-card .cell-input.price { width: 132px; padding-left: 22px; }
.rate-card .cell-input.price::placeholder { font-size: 12px; }
.rate-card .types { display: flex; flex-wrap: wrap; gap: 4px; }
.rate-card .type { font-size: 11px; font-weight: 500; color: var(--ink-3); background: var(--bg-subtle); border-radius: 999px; padding: 2px 8px; white-space: nowrap; }
.rate-card .type.plus { background: transparent; border: 1px dashed var(--border); }
@media (max-width: 1100px) { .rate-card .pp-row { grid-template-columns: 1fr 120px 60px 30px; } .rate-card .pp-row > :nth-child(2) { grid-column: 1 / -1; } .rate-card .pp-head { display: none; } }
.rate-card table { width: 100%; border-collapse: collapse; font-size: 13.5px; }
.rate-card th { text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em; color: var(--ink-4); font-weight: 600; padding: 8px 10px; border-bottom: 1px solid var(--border); white-space: nowrap; }
.rate-card th:first-child, .rate-card td:first-child { padding-left: 16px; }
.rate-card th:last-child, .rate-card td:last-child { padding-right: 16px; }
.rate-card td { padding: 7px 10px; border-bottom: 1px solid var(--border-soft); vertical-align: middle; }
.rate-card tr:last-child td { border-bottom: 0; }
.rate-card tr.inactive td { opacity: 0.45; }
.rate-card td.num, .rate-card th.num { text-align: right; font-variant-numeric: tabular-nums; }
.rate-card .cat-row td { padding-top: 14px; font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em; color: var(--ink-4); font-weight: 700; border-bottom: 0; }
.rate-card .cell-input { width: 86px; padding: 4px 8px; font: inherit; font-size: 13px; border: 1px solid transparent; border-radius: 6px; background: transparent; text-align: right; font-variant-numeric: tabular-nums; }
.rate-card .cell-input:hover { border-color: var(--border); background: var(--bg-elevated); }
.rate-card .cell-input:focus { outline: none; border-color: var(--sitewide); background: var(--bg-elevated); box-shadow: 0 0 0 3px rgba(59, 92, 255, 0.12); }
.rate-card .cell-input.slot { width: 56px; text-align: center; }
.rate-card .cell-input.text { width: 100%; min-width: 220px; text-align: left; }
.rate-card th.wide { min-width: 240px; }
.rate-card .cell-select { padding: 4px 6px; font: inherit; font-size: 12.5px; border: 1px solid transparent; border-radius: 6px; background: transparent; color: var(--ink-2); }
.rate-card .cell-select:hover { border-color: var(--border); background: var(--bg-elevated); }
.rate-card .rate-label { font-weight: 550; color: var(--ink); }
.rate-card .rate-desc { font-size: 11.5px; color: var(--ink-4); margin-top: 1px; }
.rate-card .note { font-size: 11.5px; color: var(--ink-4); font-style: italic; }
.rate-card .moment-name { font-weight: 550; color: var(--ink); cursor: pointer; }
.rate-card .moment-name:hover { text-decoration: underline; }
.rate-card .kind { display: inline-block; font-size: 10.5px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; padding: 2px 7px; border-radius: 999px; background: var(--bg-subtle); color: var(--ink-3); }
.rate-card .kind.sale { background: #FDE8E6; color: #9A2B22; }
.rate-card .kind.gifting { background: #F3E8FD; color: #6B2E9A; }
.rate-card .kind.seasonal { background: #E6F3EA; color: #2B4138; }
.rate-card .kind.brand { background: #FFF3D6; color: #8A5A00; }
.rate-card .slot-cell { text-align: center; }
.rate-card .slot-cell.override .cell-input { font-weight: 700; color: var(--ink); }
.rate-card .slot-cell .sold { font-size: 10px; color: var(--ink-4); line-height: 1; margin-bottom: 2px; }
.rate-card .slot-cell.oversold .sold { color: var(--warn); font-weight: 700; }
.rate-card .strip { display: grid; grid-template-columns: repeat(12, 1fr); gap: 2px; margin: 12px 16px 18px; }
.rate-card .strip .m { font-size: 10.5px; color: var(--ink-4); text-align: center; text-transform: uppercase; letter-spacing: 0.06em; }
.rate-card .strip-bars { position: relative; height: 10px; grid-column: 1 / -1; background: var(--bg-subtle); border-radius: 999px; overflow: hidden; }
.rate-card .strip-bars span { position: absolute; top: 0; bottom: 0; border-radius: 999px; background: var(--ink-4); opacity: 0.75; }
.rate-card .strip-bars span.sale { background: #D9534F; }
.rate-card .strip-bars span.gifting { background: #9B59B6; }
.rate-card .strip-bars span.seasonal { background: #2B4138; }
.rate-card .strip-bars span.brand { background: #E6A800; }
.rate-card .year-nav { display: inline-flex; align-items: center; gap: 6px; }
.rate-card .year-nav b { font-size: 15px; min-width: 48px; text-align: center; }
.rate-card .lines-pick { display: grid; gap: 6px; max-height: 320px; overflow: auto; padding: 4px 2px; }
.rate-card .lines-pick label { display: grid; grid-template-columns: 20px 1fr 64px; gap: 10px; align-items: center; font-size: 13px; cursor: pointer; }
.rate-card .lines-pick .qty { width: 56px; text-align: center; }
.rate-card .lines-pick .p { color: var(--ink-4); font-size: 12px; }
.rate-card .scroll-x { overflow-x: auto; }
`;

const slug = (s: string) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '').slice(0, 60);

const fmtDate = (iso: string) => {
  const d = new Date(iso + 'T00:00:00');
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
};
const daysBetween = (a: string, b: string) =>
  Math.round((new Date(b).getTime() - new Date(a).getTime()) / 86400000) + 1;

export const RateCard = () => {
  const [tab, setTab] = useState<Tab>(() => {
    try {
      return (localStorage.getItem(TAB_KEY) as Tab) || 'rates';
    } catch {
      return 'rates';
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(TAB_KEY, tab);
    } catch {
      /* private mode */
    }
  }, [tab]);

  return (
    <div className="rate-card">
      <style>{STYLES}</style>
      <div className="page-header">
        <div>
          <div className="page-eyebrow">Settings</div>
          <h1 className="page-title">Placements &amp; prices</h1>
        </div>
        <HowItWorksLink section="placements-prices" />
      </div>
      <div className="tabs" role="tablist">
        <button type="button" role="tab" className={`tab ${tab === 'rates' ? 'active' : ''}`} onClick={() => setTab('rates')}>
          Placements &amp; prices
        </button>
        <button type="button" role="tab" className={`tab ${tab === 'map' ? 'active' : ''}`} onClick={() => setTab('map')}>
          Placement map
        </button>
        <button type="button" role="tab" className={`tab ${tab === 'moments' ? 'active' : ''}`} onClick={() => setTab('moments')}>
          Moments calendar
        </button>
      </div>
      {tab === 'rates' ? <RatesTab /> : tab === 'moments' ? <MomentsTab /> : <MapEditor />}
    </div>
  );
};

export default RateCard;

/* ------------------------------------------------------------------ */
/* Tab 1: rates                                                         */
/* ------------------------------------------------------------------ */

function RatesTab() {
  const rates = useRates(true);
  const pages = useMapPages();
  // Lines with a frame on the website map show there; the others get mockup pictures.
  const onMap = useMemo(
    () => new Set((pages.data ?? []).flatMap((p) => p.sections.flatMap((s) => s.frames.map((f) => f.rate_key))).filter(Boolean)),
    [pages.data],
  );
  const [mockups, setMockups] = useState<PlacementRate | null>(null);
  const rateM = useRateMutations();
  const toast = useToast();
  const [addRate, setAddRate] = useState(false);
  const [deleteRate, setDeleteRate] = useState<PlacementRate | null>(null);

  const byCategory = useMemo(() => {
    const map = new Map<string, PlacementRate[]>();
    for (const r of rates.data ?? []) {
      if (!map.has(r.category)) map.set(r.category, []);
      map.get(r.category)!.push(r);
    }
    return ASSET_CATEGORIES.filter((c) => map.has(c)).map((c) => [c, map.get(c)!] as const);
  }, [rates.data]);

  const save = (r: PlacementRate, patch: Partial<PlacementRate>) =>
    rateM.patch.mutate(
      { id: r.id, ...patch },
      { onError: () => toast.push('Could not save — check the value and try again') },
    );

  const sellable = (rates.data ?? []).filter((r) => r.active).length;

  return (
    <>
      <div className="pp-intro">
        <p>
          Every type of placement we can use in a campaign, and what we sell it for. Tick <b>Sellable</b> for the
          ones brands can book; the <b>standard price</b> is the price of one slot — a campaign can change it on its
          partnership page. No price = “Price on request”.
        </p>
        <span className="count">{sellable} sellable</span>
        <Button variant="ghost" onClick={() => setAddRate(true)}>
          <Icon name="plus" size={13} /> Add a pack or variant
        </Button>
      </div>
      {rates.isLoading ? (
        <div className="note" style={{ padding: 16 }}>Loading…</div>
      ) : (
        byCategory.map(([cat, list]) => (
          <RateGroup
            key={cat}
            cat={cat as AssetCategory}
            list={list}
            onSave={save}
            onDelete={setDeleteRate}
            onMockups={pages.isSuccess ? (r) => (onMap.has(r.key) ? null : () => setMockups(r)) : () => null}
          />
        ))
      )}

      {addRate && <RateModal onClose={() => setAddRate(false)} />}
      {mockups && (
        <MockupsModal
          rate={(rates.data ?? []).find((r) => r.id === mockups.id) ?? mockups}
          onSave={(next) => rateM.patch.mutateAsync({ id: mockups.id, example_images: next }).catch(() => toast.push('Could not save the pictures.'))}
          onClose={() => setMockups(null)}
        />
      )}
      <ConfirmDialog
        open={!!deleteRate}
        danger
        title={`Remove ${deleteRate?.label ?? ''}?`}
        description="Placements sold as this line lose their “Sold as”. Prefer unticking “Sellable” if you may bring it back."
        confirmLabel="Remove"
        busy={rateM.remove.isPending}
        onClose={() => setDeleteRate(null)}
        onConfirm={() => {
          if (!deleteRate) return;
          rateM.remove.mutate(deleteRate.id, { onSuccess: () => setDeleteRate(null) });
        }}
      />
    </>
  );
}

/** One category: uppercase title above the card (coloured dot), then one
 *  row per line — same pattern as the Black Friday pages. */
function RateGroup({
  cat,
  list,
  onSave,
  onDelete,
  onMockups,
}: {
  cat: AssetCategory;
  list: PlacementRate[];
  onSave: (r: PlacementRate, patch: Partial<PlacementRate>) => void;
  onDelete: (r: PlacementRate) => void;
  /** Opener for the line's mockup pictures; null when it's on the website map. */
  onMockups: (r: PlacementRate) => (() => void) | null;
}) {
  return (
    <div className="pp-cat" style={{ '--sec': CATEGORY_COLOR[cat] } as React.CSSProperties}>
      <div className="section-head">
        <div className="section-title pp-cat-title"><span className="dot" />{cat}</div>
      </div>
      <Card className="pp-cat-card">
        <div className="pp-row pp-head">
          <span>Placement</span>
          <span>Placement type in mosy</span>
          <span className="num">Standard price · max per brand</span>
          <span style={{ textAlign: 'center' }}>Sellable</span>
          <span />
        </div>
        {list.map((r) => (
          <div key={r.id} className={`pp-row${r.active ? '' : ' off'}`}>
            <div>
              <CommitInput className="cell-input name" value={r.label} onCommit={(v) => v.trim() && onSave(r, { label: v.trim() })} />
              <CommitInput
                className="cell-input desc"
                value={r.description}
                placeholder="One line brands read…"
                onCommit={(v) => onSave(r, { description: v })}
              />
              <div className="perday" title="Expected per day — brands see it next to the price. A campaign or a single day can change it.">
                <span>Per day</span>
                {([0, 1] as const).map((i) => (
                  <label key={i}>
                    <CommitInput
                      type="number"
                      min={0}
                      className="cell-input"
                      value={(i === 0 ? r.stat_1 : r.stat_2) != null ? String(i === 0 ? r.stat_1 : r.stat_2) : ''}
                      placeholder="—"
                      onCommit={(v) => onSave(r, { [i === 0 ? 'stat_1' : 'stat_2']: toCount(v) })}
                    />
                    {STAT_LABEL[statLabelsFor(r.category)[i]].toLowerCase()}
                  </label>
                ))}
              </div>
              {(() => {
                const open = onMockups(r);
                return open ? (
                  <button type="button" className="link" style={{ fontSize: 11.5, marginLeft: 6 }} onClick={open}>
                    {r.image_count ? `Mockup pictures (${r.image_count})` : 'Add mockup pictures…'}
                  </button>
                ) : null;
              })()}
            </div>
            <div className="types">
              {r.asset_type ? <span className="type">{r.asset_type}</span> : <span className="note">—</span>}
              {(r.pack_types ?? []).map((t) => <span key={t} className="type plus">+ {t}</span>)}
            </div>
            <div className="num">
              <span className="eur">
                <span className="cur">€</span>
                <CommitInput
                  type="number"
                  min={0}
                  className="cell-input price"
                  value={parseFloat(r.price) > 0 ? String(parseFloat(r.price)) : ''}
                  placeholder="On request"
                  onCommit={(v) => onSave(r, { price: v === '' ? '0' : v })}
                />
              </span>
              <span className="maxpb" title="Most slots one brand may book of this in one campaign. Empty = no maximum.">
                max
                <CommitInput
                  type="number"
                  min={1}
                  className="cell-input"
                  value={r.max_per_brand != null ? String(r.max_per_brand) : ''}
                  placeholder="–"
                  onCommit={(v) => onSave(r, { max_per_brand: v === '' ? null : Math.max(1, Math.floor(Number(v))) })}
                />
                per brand
              </span>
            </div>
            <div style={{ textAlign: 'center' }}>
              <Checkbox checked={r.active} onChange={(v) => onSave(r, { active: v })} aria-label="Sellable" />
            </div>
            <div style={{ textAlign: 'right' }}>
              <IconButton aria-label={`Remove ${r.label}`} onClick={() => onDelete(r)}>
                <Icon name="x" size={13} />
              </IconButton>
            </div>
          </div>
        ))}
      </Card>
    </div>
  );
}

/** Standard mockup pictures of a line that isn't on the website map. */
function MockupsModal({ rate, onSave, onClose }: { rate: PlacementRate; onSave: (next: string[]) => Promise<unknown>; onClose: () => void }) {
  return (
    <Modal open onClose={onClose} title={`Mockup pictures — ${rate.label}`} maxWidth={640} footer={<Button variant="primary" onClick={onClose}>Done</Button>}>
      <p className="note" style={{ marginTop: 0 }}>
        What brands see for this placement on a booking page (e.g. an example newsletter, or the post + story + reel).
        Every campaign shows these, unless its partnership page has its own pictures.
      </p>
      <MockupGallery images={rate.example_images ?? []} onChange={onSave} />
    </Modal>
  );
}

/** Extra sellable line on top of the one-per-type list: a variant (e.g.
 *  "Feature in a newsletter" next to "Dedicated newsletter") or a pack of
 *  several placement types sold as one item (e.g. the Social pack). */
function RateModal({ onClose }: { onClose: () => void }) {
  const rateM = useRateMutations();
  const toast = useToast();
  const [label, setLabel] = useState('');
  const [category, setCategory] = useState<AssetCategory>('Social Media');
  const [assetType, setAssetType] = useState('');
  const [packTypes, setPackTypes] = useState<string[]>([]);
  const [price, setPrice] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');

  const submit = () => {
    if (!label.trim()) return setError('Give it a name.');
    if (!assetType) return setError('Pick the placement type it is sold on.');
    rateM.create.mutate(
      {
        key: slug(label),
        label: label.trim(),
        category,
        asset_type: assetType,
        pack_types: packTypes.filter((t) => t !== assetType),
        price: price || '0',
        period: 'campaign',
        slots_per_moment: 1,
        description,
        price_note: '',
      },
      {
        onSuccess: () => {
          toast.push('Added to Placements & prices');
          onClose();
        },
        onError: (e: unknown) => {
          const data = (e as { response?: { data?: Record<string, string[]> } })?.response?.data;
          setError(data ? Object.values(data).flat().join(' ') : 'Could not save.');
        },
      },
    );
  };

  const others = ASSET_TYPES_BY_CATEGORY[category].filter((t) => t !== assetType);

  return (
    <Modal
      open
      onClose={onClose}
      title="Add a pack or variant"
      maxWidth={560}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={rateM.create.isPending}>Add</Button>
        </>
      }
    >
      <div style={{ display: 'grid', gap: 12 }}>
        <div className="note" style={{ fontStyle: 'normal' }}>
          Every placement type already has its own line. Add one here when the same type is sold in two ways (e.g. a
          dedicated newsletter or a feature in one), or when several placements are sold as one item (e.g. a social pack).
        </div>
        <Field label="Name brands read" required>
          <input className="input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Social pack" autoFocus />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Channel">
            <select className="input" value={category} onChange={(e) => { setCategory(e.target.value as AssetCategory); setAssetType(''); setPackTypes([]); }}>
              {ASSET_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Sold on (placement type)" required hint="The placement that carries the brand slots.">
            <select className="input" value={assetType} onChange={(e) => setAssetType(e.target.value)}>
              <option value="">— pick one —</option>
              {ASSET_TYPES_BY_CATEGORY[category].map((t) => <option key={t}>{t}</option>)}
            </select>
          </Field>
        </div>
        {assetType && others.length > 0 && (
          <Field label="Comes with (optional)" hint="Placements of these types on the same day are part of the item.">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              {others.map((t) => (
                <label key={t} style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontSize: 13, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={packTypes.includes(t)}
                    onChange={(e) => setPackTypes(e.target.checked ? [...packTypes, t] : packTypes.filter((x) => x !== t))}
                  />
                  {t}
                </label>
              ))}
            </div>
          </Field>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 12 }}>
          <Field label="Standard price (€)">
            <input className="input" type="number" min={0} value={price} placeholder="On request" onChange={(e) => setPrice(e.target.value)} />
          </Field>
          <Field label="One line brands read">
            <input className="input" value={description} onChange={(e) => setDescription(e.target.value)} />
          </Field>
        </div>
        {error && <div className="note" style={{ color: 'var(--warn)' }}>{error}</div>}
      </div>
    </Modal>
  );
}

/* ------------------------------------------------------------------ */

function MomentsTab() {
  const [year, setYear] = useState(new Date().getFullYear() + 1);
  const moments = useMoments(year);
  const rates = useRates();
  const momentM = useMomentMutations();
  const toast = useToast();
  const [edit, setEdit] = useState<Moment | 'new' | null>(null);

  const cols = useMemo(() => (rates.data ?? []).filter((r) => r.active), [rates.data]);

  const setSlot = (m: Moment, key: string, raw: string) => {
    const next = { ...m.slots };
    if (raw === '') delete next[key];
    else next[key] = Math.max(0, parseInt(raw, 10) || 0);
    momentM.patch.mutate({ id: m.id, slots: next }, { onError: () => toast.push('Could not save the slot count') });
  };

  const yearStart = new Date(year, 0, 1).getTime();
  const yearLen = new Date(year + 1, 0, 1).getTime() - yearStart;
  const pct = (iso: string) => ((new Date(iso + 'T00:00:00').getTime() - yearStart) / yearLen) * 100;

  return (
    <Card>
      <CardHead
        title={
          <span className="year-nav">
            <IconButton aria-label="Previous year" onClick={() => setYear((y) => y - 1)}><Icon name="chevronLeft" size={14} /></IconButton>
            <b>{year}</b>
            <IconButton aria-label="Next year" onClick={() => setYear((y) => y + 1)}><Icon name="chevronRight" size={14} /></IconButton>
            <span style={{ fontWeight: 500, marginLeft: 6 }}>moments</span>
          </span>
        }
        sub="The commercial calendar we sell placements against. Each cell is how many of that placement we offer in the moment; bold means you changed it from the rate-card default. Leave a cell empty to go back to the default. “Sold” counts the slots already on brand deal sheets."
        action={
          <Button variant="primary" onClick={() => setEdit('new')}>
            <Icon name="plus" size={13} /> Add moment
          </Button>
        }
      />
      <div className="strip">
        {MONTHS.map((m) => <div key={m} className="m">{m}</div>)}
        <div className="strip-bars">
          {(moments.data ?? []).map((m) => (
            <span
              key={m.id}
              className={m.kind}
              title={`${m.name} · ${fmtDate(m.start_date)} – ${fmtDate(m.end_date)}`}
              style={{ left: `${pct(m.start_date)}%`, width: `${Math.max(0.6, pct(m.end_date) - pct(m.start_date))}%` }}
            />
          ))}
        </div>
      </div>
      {moments.isLoading ? (
        <div className="note" style={{ padding: 16 }}>Loading…</div>
      ) : (moments.data ?? []).length === 0 ? (
        <div className="note" style={{ padding: '8px 16px 16px' }}>
          No moments for {year} yet. Add the first one, or switch year.
        </div>
      ) : (
        <div className="scroll-x">
          <table>
            <thead>
              <tr>
                <th>Moment</th>
                <th>Dates</th>
                {cols.map((r) => (
                  <th key={r.key} style={{ textAlign: 'center' }} title={r.label}>
                    {shortLabel(r)}
                  </th>
                ))}
                <th className="num">Total slots</th>
              </tr>
            </thead>
            <tbody>
              {(moments.data ?? []).map((m) => (
                <tr key={m.id}>
                  <td>
                    <div className="moment-name" onClick={() => setEdit(m)}>{m.name}</div>
                    <div style={{ marginTop: 3 }}><span className={`kind ${m.kind}`}>{KIND_LABEL[m.kind]}</span>{m.campaign_name && <span className="note" style={{ marginLeft: 8 }}>↳ {m.campaign_name}</span>}</div>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {fmtDate(m.start_date)} – {fmtDate(m.end_date)}
                    <div className="rate-desc">{daysBetween(m.start_date, m.end_date)} days</div>
                  </td>
                  {cols.map((r) => (
                    <td key={r.key} className={`slot-cell ${r.key in m.slots ? 'override' : ''} ${(m.slots_sold[r.key] ?? 0) > (m.slots_effective[r.key] ?? 0) ? 'oversold' : ''}`}>
                      {(m.slots_sold[r.key] ?? 0) > 0 && (
                        <div className="sold" title="Slots already on brand deal sheets">{m.slots_sold[r.key]} sold</div>
                      )}
                      <CommitInput
                        type="number"
                        min={0}
                        className="cell-input slot"
                        value={r.key in m.slots ? String(m.slots[r.key]) : ''}
                        placeholder={String(m.slots_effective[r.key] ?? r.slots_per_moment)}
                        onCommit={(v) => setSlot(m, r.key, v)}
                      />
                    </td>
                  ))}
                  <td className="num"><b>{m.slots_total}</b></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {edit && (
        <MomentModal moment={edit === 'new' ? null : edit} year={year} onClose={() => setEdit(null)} />
      )}
    </Card>
  );
}

const shortLabel = (r: PlacementRate) =>
  r.label
    .replace('Homepage ', '')
    .replace('Instagram ', 'IG ')
    .replace(' · full width', ' 100%')
    .replace(' · half width', ' 50%')
    .replace('Feature in a campaign newsletter', 'NL feature')
    .replace('Dedicated newsletter', 'NL dedicated')
    .replace('Featured products on a collection', 'Coll. featured')
    .replace('Banner on product pages', 'PDP banner');

function MomentModal({ moment, year, onClose }: { moment: Moment | null; year: number; onClose: () => void }) {
  const momentM = useMomentMutations();
  const toast = useToast();
  const [name, setName] = useState(moment?.name ?? '');
  const [kind, setKind] = useState<MomentKind>(moment?.kind ?? 'seasonal');
  const [start, setStart] = useState(moment?.start_date ?? `${year}-01-01`);
  const [end, setEnd] = useState(moment?.end_date ?? `${year}-01-07`);
  const [notes, setNotes] = useState(moment?.notes ?? '');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState('');

  const submit = () => {
    if (!name.trim()) return setError('Give the moment a name.');
    if (end < start) return setError('The end date is before the start date.');
    const payload = { name: name.trim(), kind, start_date: start, end_date: end, notes, year: parseInt(start.slice(0, 4), 10) };
    const opts = {
      onSuccess: () => {
        toast.push(moment ? 'Moment saved' : 'Moment added');
        onClose();
      },
      onError: () => setError('Could not save the moment.'),
    };
    if (moment) momentM.patch.mutate({ id: moment.id, ...payload }, opts);
    else momentM.create.mutate(payload, opts);
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={moment ? `Edit ${moment.name}` : 'New moment'}
      maxWidth={520}
      footer={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
          {moment && (
            <Button variant="ghost" style={{ color: 'var(--warn)' }} onClick={() => setConfirmDelete(true)}>
              Delete moment
            </Button>
          )}
          <div style={{ flex: 1 }} />
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={momentM.create.isPending || momentM.patch.isPending}>
            {moment ? 'Save changes' : 'Add moment'}
          </Button>
        </div>
      }
    >
      <div style={{ display: 'grid', gap: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
          <Field label="Name" required>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Black Friday" autoFocus={!moment} />
          </Field>
          <Field label="Kind">
            <select className="input" value={kind} onChange={(e) => setKind(e.target.value as MomentKind)}>
              {KINDS.map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
            </select>
          </Field>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Starts">
            <input className="input" type="date" value={start} onChange={(e) => setStart(e.target.value)} />
          </Field>
          <Field label="Ends">
            <input className="input" type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
          </Field>
        </div>
        <Field label="Notes" hint="Anything the team should know: themes, brands already in talks, constraints.">
          <textarea className="input" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
        {moment?.campaign_name && (
          <div className="note">Linked to the campaign “{moment.campaign_name}”.</div>
        )}
        {error && <div className="note" style={{ color: 'var(--warn)' }}>{error}</div>}
      </div>
      <ConfirmDialog
        open={confirmDelete}
        danger
        title={`Delete ${moment?.name ?? 'this moment'}?`}
        description="Removes it from the calendar. Campaigns and placements are not touched."
        confirmLabel="Delete"
        busy={momentM.remove.isPending}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() => moment && momentM.remove.mutate(moment.id, { onSuccess: onClose })}
      />
    </Modal>
  );
}
